import {
  CustomButton,
  CustomCheckbox,
  CustomForm,
  CustomFormItem,
  CustomInput,
  CustomPassword,
  Text,
  useText,
} from '@src/components';
import { Form } from 'antd';
import LoginLayout from '@src/layout/LoginLayout';
import { NavLink } from 'react-router-dom';
import { loginApplicationLink } from '@src/utils/keys';
import '@src/styles/login/login.scss';

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { login, removeToken, loginOtpDisable, loginOtpExclude } from '@src/store/slice/authSlice';
import { resetCurrentUser, resetUserInfo } from '@src/store/slice/userSlice';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PropTypes from 'prop-types';
import useSubmitLock from '@src/hooks/useSubmitLock';

const Login = ({ history }) => {
  const dispatch = useDispatch();
  const { token, adminToken, MigrationId } = useSelector((state) => state?.auth);
  const [isLoading, setIsLoading] = useState(false);
  const { isSubmitting, runWithLock } = useSubmitLock();

  const [form] = Form.useForm();
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    const captchaRenderDom = document.querySelector('#captcha-render-dom');
    if (captchaRenderDom) {
      captchaRenderDom.style.display = 'block';
    }
    return () => {
      if (captchaRenderDom) {
        captchaRenderDom.style.display = 'none';
      }
    };
  }, []);

  useEffect(() => {
    if (token) {
      let loginTime = localStorage.getItem('login:time');
      if (!loginTime) {
        loginTime = 0;
      }
      const now = new Date().getTime();
      const diff = 108000 < Math.ceil((now - loginTime) / 1000);
      if (!diff) {
        faceValueIsExists();
        history.push('/dashboard');
      }
    }
  }, [history, token]);

  useEffect(() => {
    (async () => {
      if (!adminToken) {
        await dispatch(removeToken());
        await dispatch(resetUserInfo());
        await dispatch(resetCurrentUser());
      }
    })();
  }, []);

  const faceValueIsExists = () => {
    if (localStorage.getItem('faceValue')) {
      localStorage.setItem('faceValue', 0);
    } else {
      localStorage.setItem('faceValue', 1);
    }
  };

  const handleLoginResponse = useCallback(
    async (action, values) => {
      const data = action?.payload?.data || {};

      const { isMaintanence, hasNoCaptchaAndOtp, isOtpExclude } = data;

      if (isOtpExclude) {
        const body = {
          userName: values?.userName,
          ...(MigrationId && { migrationId: MigrationId }),
        };
        console.log('body', body);
        await dispatch(loginOtpExclude(body));
        history?.push('/dashboard');
        return;
      }

      if (isMaintanence && hasNoCaptchaAndOtp) {
        await dispatch(loginOtpDisable({ userName: values?.userName }));
        history?.push('/dashboard');
        return;
      }

      if (isMaintanence || hasNoCaptchaAndOtp) {
        history?.push('/502');
        return;
      }

      if (!login.fulfilled.match(action)) return;

      const prevPath = history?.location?.state?.from?.pathname;
      if (prevPath && prevPath !== '/') {
        history?.push(`/sms-verification?redirect=${encodeURIComponent(prevPath)}`);
      } else {
        history?.push('/sms-verification');
      }
    },
    [dispatch, history],
  );

  const onFinish = useCallback(
    (values) =>
      runWithLock(async () => {
        setIsLoading(true);

        try {
          const token = await executeRecaptcha('login');
          const body = {
            userName: values?.userName,
            password: values?.password,
            captchaKey: token,
          };

          const action = await dispatch(login(body));
          faceValueIsExists();
          await handleLoginResponse(action, values);
        } finally {
          setIsLoading(false);
        }
      }),
    [dispatch, executeRecaptcha, handleLoginResponse, runWithLock],
  );

  const loginUsernameInputForgotPassword = useText('loginUsernameInputForgotPassword');
  const loginPasswordInput = useText('loginPasswordInput');

  return (
    <LoginLayout isBackButton={false} className="login">
      {executeRecaptcha ? (
        <>
          <div className="rightSideLoginFormTitle">
            <Text t="ticketOnlineLogin" />
          </div>
          <CustomForm
            name="login"
            form={form}
            initialValues={{}}
            onFinish={onFinish}
            layout={'vertical'}
          >
            <CustomFormItem
              label={<Text t="loginUsernameInputForgotPassword" />}
              name="userName"
              rules={[
                { required: true, message: <Text t="loginUsernameInputRequired" /> },
                { type: 'string', max: 50 },
              ]}
            >
              <CustomInput
                id="login-main-userName-input"
                placeholder={loginUsernameInputForgotPassword}
                maxLength={50}
              />
            </CustomFormItem>

            <CustomFormItem
              label={<Text t="loginPassword" />}
              name="password"
              rules={[
                { required: true, max: 20, message: <Text t="loginPasswordInputRequired" /> },
                { type: 'string' },
              ]}
            >
              <CustomPassword
                id="login-main-password-passwordInput"
                placeholder={loginPasswordInput}
                maxLength={20}
              />
            </CustomFormItem>
            <CustomFormItem
              label={false}
              name="checkText"
              className="check-form-item disabled-check-form-item "
              valuePropName="checked"
            >
              <CustomCheckbox id="login-main-checkText-checkbox">
                <Text t="iAgreeToBeContactedViaEMail" />
              </CustomCheckbox>
            </CustomFormItem>

            <CustomFormItem>
              <CustomButton
                id="login-main-primary-btn"
                type="primary"
                htmlType="submit"
                disabled={isLoading || isSubmitting}
                style={{ width: '100%' }}
              >
                <span className="submit">
                  <Text t="loginBtn" />
                </span>
              </CustomButton>
            </CustomFormItem>
          </CustomForm>

          <div
            style={{
              marginTop: '-0.25rem',
              marginBottom: '-0.25rem',
            }}
          >
            <div className="forgetPasswordContent">
              <NavLink to={'/forgot-password'}>
                <span className="forgetPassword">
                  <Text t="forgotPassword" />
                </span>
              </NavLink>
            </div>
          </div>

          <div className="dropdown-divider loginDivider" />

          <div className="">
            <div className="">
              <h5 className="registerText">
                <Text t="ifNotMemberYet" />
                <a
                  className="registerButton"
                  href={loginApplicationLink}
                  target={'_blank'}
                  rel="noreferrer"
                >
                  <Text t="clickToApply" />
                </a>
              </h5>
            </div>
          </div>

          <div className="dropdown-divider loginDivider" />
        </>
      ) : null}
    </LoginLayout>
  );
};

Login.propTypes = {
  history: PropTypes.any,
};

export default Login;
